import request from '@/utils/request';
import { AxiosPromise } from 'axios';
import { LoginData, LoginResult, VerifyCodeResult, TenantEntry, TenantRelation } from './types';
import { UserInfo } from '@/api/system/user/types';
import { loadTenantListCache } from '@/utils/tenantListCache';

// pc端固定客户端授权id
const clientId = import.meta.env.VITE_APP_CLIENT_ID;

/**
 * @param data {LoginData}
 * @returns
 */
export function login(data: LoginData): AxiosPromise<LoginResult> {
  const params = {
    ...data,
    clientId: data.clientId || clientId,
    grantType: data.grantType || 'password',
  };
  return request({
    url: '/b/auth/login',
    headers: {
      isToken: false,
      repeatSubmit: false,
    },
    method: 'post',
    data: params,
  });
}

// 注册方法
export function register(data: any) {
  const params = {
    ...data,
    clientId,
    grantType: 'password',
  };
  return request({
    url: '/auth/register',
    headers: {
      isToken: false,
      isEncrypt: true,
      repeatSubmit: false,
    },
    method: 'post',
    data: params,
  });
}

/**
 * 注销
 */
export function logout() {
  if (import.meta.env.VITE_APP_SSE === 'true') {
    request({
      url: '/resource/sse/close',
      method: 'get',
    });
  }
  return request({
    url: '/b/auth/logout',
    method: 'post',
  });
}

/**
 * 获取验证码
 */
export function getCodeImg(): AxiosPromise<VerifyCodeResult> {
  return request({
    url: '/auth/code',
    headers: {
      isToken: false,
    },
    method: 'get',
    timeout: 20000,
  });
}

export type ScanLoginStatus = 'WAIT_SCAN' | 'SCANNED' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';

export interface ScanLoginQrCodeData {
  qrToken: string;
  qrContent: string;
  pollSecret: string;
  expireIn: number;
}

export interface ScanLoginStatusData {
  status: ScanLoginStatus;
  statusMessage: string;
  remainingSeconds: number;
}

/** 创建扫码登录二维码会话 */
export function createScanLoginQrCode(): AxiosPromise<ScanLoginQrCodeData> {
  return request({
    url: '/auth/scan-login/qrcode',
    headers: {
      isToken: false,
      repeatSubmit: false,
    },
    method: 'post',
  });
}

/** 查询扫码登录二维码状态 */
export function getScanLoginStatus(qrToken: string, pollSecret: string): AxiosPromise<ScanLoginStatusData> {
  return request({
    url: `/auth/scan-login/qrcode/${encodeURIComponent(qrToken)}/status`,
    headers: {
      isToken: false,
      'X-Scan-Login-Secret': pollSecret,
      hideErrorNotification: true,
    },
    method: 'get',
  });
}

/**
 * 第三方登录
 */
export function callback(data: LoginData): AxiosPromise<any> {
  const loginData = {
    ...data,
    clientId,
    grantType: 'social',
  };
  return request({
    url: '/auth/social/callback',
    method: 'post',
    data: loginData,
  });
}

// 获取用户详细信息
export function getInfo(): AxiosPromise<UserInfo> {
  return request({
    url: '/system/user/getInfo',
    method: 'get',
  });
}

// 获取租户列表
export function tenantListApi(isToken: boolean, options?: { hideErrorNotification?: boolean }): AxiosPromise<TenantEntry[]> {
  return request({
    url: '/b/auth/tenant/list',
    headers: {
      isToken,
      hideErrorNotification: options?.hideErrorNotification,
    },
    method: 'get',
  });
}

/** 复用登录会话内的完整视角列表，刷新或切换后仍可读取。 */
export function getTenantList(options?: { hideErrorNotification?: boolean }): Promise<TenantEntry[]> {
  return loadTenantListCache(async () => {
    const { data } = await tenantListApi(true, options);
    return data;
  });
}

/** 发送手机号登录验证码。 */
export function getSmsCode(data: { phoneNumber: string }): AxiosPromise<UserInfo> {
  return request({
    url: '/resource/sms/code',
    headers: {
      isToken: false,
      repeatSubmit: false,
    },
    method: 'post',
    data,
  });
}
// 发送邮箱验证码
export function getEmailCode(params): AxiosPromise<UserInfo> {
  return request({
    url: '/resource/email/code',
    method: 'get',
    params,
  });
}
// 选择租户进入(需要token)
export function tenantWitchApi(data: TenantRelation, options?: { hideErrorNotification?: boolean }): AxiosPromise<UserInfo> {
  return request({
    url: '/b/auth/tenant/switch',
    headers: {
      hideErrorNotification: options?.hideErrorNotification,
    },
    method: 'post',
    data,
  });
}

// 获取当前租户前端国际化配置
export function getTenantI18nConfig(options?: { hideErrorNotification?: boolean }): AxiosPromise<any> {
  return request({
    url: '/b/common/i18n/config',
    headers: {
      hideErrorNotification: options?.hideErrorNotification,
    },
    method: 'get',
  });
}
